import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface PendingNavigation {
  gameId: string;
  gameTitle: string;
  gameImage: string;
  gameProvider?: string;
}

interface JackpotOptInPromptContextType {
  isPromptOpen: boolean;
  pendingNavigation: PendingNavigation | null;
  showPrompt: (navigation: PendingNavigation) => void;
  closePrompt: () => void;
  onOptInAndPlay: () => void;
  onPlayWithoutOptIn: () => void;
  setNavigationCallback: (callback: (gameId: string) => void) => void;
}

const JackpotOptInPromptContext = createContext<JackpotOptInPromptContextType | undefined>(undefined);

export const useJackpotOptInPrompt = () => {
  const context = useContext(JackpotOptInPromptContext);
  if (!context) {
    throw new Error('useJackpotOptInPrompt must be used within a JackpotOptInPromptProvider');
  }
  return context;
};

export const JackpotOptInPromptProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const navigationCallbackRef = useRef<((gameId: string) => void) | null>(null);

  const showPrompt = useCallback((navigation: PendingNavigation) => {
    setPendingNavigation(navigation);
    setIsPromptOpen(true);
  }, []);

  const closePrompt = useCallback(() => {
    setIsPromptOpen(false);
    setPendingNavigation(null);
  }, []);

  const onOptInAndPlay = useCallback(() => {
    if (pendingNavigation && navigationCallbackRef.current) {
      navigationCallbackRef.current(pendingNavigation.gameId);
    }
    closePrompt();
  }, [pendingNavigation, closePrompt]);

  const onPlayWithoutOptIn = useCallback(() => {
    if (pendingNavigation && navigationCallbackRef.current) {
      navigationCallbackRef.current(pendingNavigation.gameId);
    }
    closePrompt();
  }, [pendingNavigation, closePrompt]);

  const setNavigationCallback = useCallback((callback: (gameId: string) => void) => {
    navigationCallbackRef.current = callback;
  }, []);

  return (
    <JackpotOptInPromptContext.Provider
      value={{
        isPromptOpen,
        pendingNavigation,
        showPrompt,
        closePrompt,
        onOptInAndPlay,
        onPlayWithoutOptIn,
        setNavigationCallback,
      }}
    >
      {children}
    </JackpotOptInPromptContext.Provider>
  );
};
